import { subDays } from 'date-fns';
import React, { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  EventDTO,
  IndicatorDTOHackDataSourceQueryReasonEnum,
  ResultDataPointDTO,
  SloDTOFromJSON,
} from '@atlassiansox/phobos-client-ts';
import { useQuery } from '@tanstack/react-query';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import { BooleanParam, useQueryParam } from 'use-query-params';
import Button from '@atlaskit/button/new';
import ChildIssuesIcon from '@atlaskit/icon/glyph/child-issues';

import { apiFetch } from '@/services';
import SloPageData from '@/components/composed/new-slo-views/expanded/data';
import {
  useFetchErrorController,
  useLocationController,
  useNavigationController,
} from '@/controllers';
import SloPageHeader from '@/components/composed/new-slo-views/expanded/header';
import SloPageSummary from '@/components/composed/new-slo-views/expanded/summary';
import LowTrafficHelper from '@/components/composed/new-slo-views/expanded/low-traffic-helper';
import {
  CapabilityEntityLink,
  KeyValue,
  Item,
  SkeletonBlock,
  useActions,
  ExternalLinkAction,
} from '@/components/primitive';
import {
  breakWord,
  flexStart,
  spaceGapMinimum,
  spaceTopComfortable,
  spaceTopCozy,
  spaceTopMinimum,
} from '@/styles';
import { joinEventsToSlo } from '@/ui';
import { SloEventsFromSingleSLO } from '@/api';
import { WithPageTitle } from '@/components/primitive/with-page-title';
import RevisionLogs from '@/components/primitive/revision-logs';
import { ExperienceErrorBoundary } from '@/components/primitive/error-boundary';
import { FetchError } from '@/services/api-service/api-service';
import { applySloDefaults, isHighCardinality } from '@/views/slo-view/utils';
import { querySpec } from '@/services/api-service/api-queries';
import { stringReplaceLink } from '@/utils';
import { SLOIndicatorWithGroupType } from '@/components/composed/slo-group/types';
import { getPreviousDay, getStartOfDayUTC } from '@/controllers/slo-graph-controller/utils';
import { getSLOLifecycleStatus } from '@/components/composed/new-slo-views/common/utils';
import { isIndicatorTypeMimir } from '@/components/composed/slo-group/utils';
import { cohortHierarchyToBFSData } from '@/components/composed/slo-group/slo-panel/utils';
import { SloQualityCheckerId } from '@/constants';

import * as styles from './styles';
import LowPriorityWarning from './low-priority-warning';
import HighCardinalityWarning from './high-cardinality-warning';

const SLOView: React.FC = () => {
  const { publicId: publicIdParam } = useParams<{ publicId: string }>();
  const publicId = publicIdParam ?? '';

  const { onError } = useFetchErrorController();
  const { onNavigation } = useNavigationController();
  const { isSLOGroupMemberPage } = useLocationController();

  const { data: rawSloData, error, isInitialLoading: sloIsLoading } = useQuery(
    ['slo', publicId],
    () =>
      apiFetch(`${isSLOGroupMemberPage ? '/slogroup' : ''}/slo/${publicId}?includeDeleted=true`),
    { onError }
  );

  const isMimir = isIndicatorTypeMimir(rawSloData?.indicator);

  const { data: rawEventData } = useQuery(
    ['events', publicId],
    () => apiFetch(`/slo/${publicId}/events`),
    {
      onError,
      enabled: rawSloData != null && !rawSloData.isSnapshot && !isMimir,
    }
  );
  const events: EventDTO[] = (Object.values(rawEventData || {}).flat() || []) as EventDTO[];

  const sloGroupPublicId = rawSloData?.indicator?.sloGroupPublicId || publicId.split(':')[0];
  const indicator: SLOIndicatorWithGroupType = rawSloData?.indicator;

  const latestDataPointTimestamp =
    rawSloData?.performance?.dataPoints &&
    rawSloData.performance.dataPoints[rawSloData.performance.dataPoints.length - 1]?.timestamp;
  const latestDataPointUTC =
    latestDataPointTimestamp && getStartOfDayUTC(new Date(latestDataPointTimestamp)).getTime();
  const twoDaysAgoTimestamp = getStartOfDayUTC(subDays(new Date(), 2)).getTime();
  const fetchInterimResults = !isMimir && latestDataPointUTC >= twoDaysAgoTimestamp;
  const isMissingPreviousDayResults = latestDataPointUTC === twoDaysAgoTimestamp;

  const { data: rawInterimData, error: interimError } = useQuery(
    ['interim', publicId],
    () => apiFetch(`/slo/${publicId}/getInterimResults`),
    {
      onError,
      enabled: fetchInterimResults,
      cacheTime: 0,
      retry: false,
    }
  );

  const { queryKey: sloIncidentsKey, path: sloIncidentsPath } = querySpec('hot.slo.{id}', {
    id: publicId,
  });
  const { data: sloIncidentsData } = useQuery(sloIncidentsKey, () => apiFetch(sloIncidentsPath), {
    onError,
    // only fetch if SLO data is loaded so we know if it's a group member or not
    enabled: !sloIsLoading,
  });

  const { queryKey: lowTrafficStatusKey, path: lowTrafficStatusPath } = querySpec(
    'slo.{id}.lowTrafficStatus',
    {
      id: publicId,
    }
  );
  const { data: lowTrafficStatusData } = useQuery(
    lowTrafficStatusKey,
    () => apiFetch(lowTrafficStatusPath),
    {
      onError,
      enabled: rawSloData != null && !rawSloData.isSnapshot && !isMimir,
    }
  );

  const data = useMemo(() => {
    let sloData;
    if (rawSloData) {
      sloData = joinEventsToSlo(
        SloEventsFromSingleSLO(rawEventData ?? {}),
        sloIncidentsData || [],
        applySloDefaults(SloDTOFromJSON(rawSloData))
      );
    }

    if (interimError || (rawInterimData && rawInterimData.length === 0)) {
      return {
        ...sloData,
        interimResults: {
          error: interimError,
          isLoading: false,
          isMissingPreviousDayResults,
        },
      };
    }

    let interimTimestamp;
    let interimGoodCount;
    let interimBadCount;
    let interimResult;
    if (rawInterimData && rawInterimData.length > 0) {
      rawInterimData.forEach((interimDataPoint: ResultDataPointDTO) => {
        const dataPointTimestamp = getStartOfDayUTC(new Date(interimDataPoint.timestamp)).getTime();
        const currentDayTimestamp = getStartOfDayUTC(new Date()).getTime();
        const previousDayTimestamp = getPreviousDay(new Date()).getTime();

        // Skip data points if they are not for the current/previous day, or if they are for the previous day but we already have its result
        if (
          (dataPointTimestamp !== currentDayTimestamp &&
            dataPointTimestamp !== previousDayTimestamp) ||
          (dataPointTimestamp === previousDayTimestamp && !isMissingPreviousDayResults)
        ) {
          return;
        }

        // Filter out any existing data points for the current/previous day to prevent duplicates
        if (dataPointTimestamp === previousDayTimestamp) {
          rawSloData.performance.dataPoints = rawSloData.performance.dataPoints.filter(
            (dataPoint: ResultDataPointDTO) =>
              getStartOfDayUTC(new Date(dataPoint.timestamp)).getTime() !== previousDayTimestamp
          );
        } else if (dataPointTimestamp === currentDayTimestamp) {
          rawSloData.performance.dataPoints = rawSloData.performance.dataPoints.filter(
            (dataPoint: ResultDataPointDTO) =>
              getStartOfDayUTC(new Date(dataPoint.timestamp)).getTime() !== currentDayTimestamp
          );

          interimTimestamp = new Date(interimDataPoint.timestamp).getTime();
          interimGoodCount = interimDataPoint.rolling?.goodCount ?? 0;
          interimBadCount = interimDataPoint.rolling?.badCount ?? 0;
          interimResult = interimDataPoint.rolling?.result ?? 0;
        }

        const newDatapoint = { ...interimDataPoint };
        newDatapoint.timestamp = getStartOfDayUTC(new Date(newDatapoint.timestamp));
        rawSloData.performance.dataPoints.push(newDatapoint);
      });

      if (rawSloData.performance.dataPoints.length > 28) {
        rawSloData.performance.dataPoints.slice(-28);
      }

      sloData = joinEventsToSlo(
        SloEventsFromSingleSLO(rawEventData ?? {}),
        sloIncidentsData || [],
        applySloDefaults(SloDTOFromJSON(rawSloData))
      );

      return {
        ...sloData,
        interimResults: {
          timestamp: interimTimestamp,
          goodCount: interimGoodCount,
          badCount: interimBadCount,
          totalCount: (interimGoodCount ?? 0) + (interimBadCount ?? 0),
          result: interimResult,
          error: false,
          isLoading: false,
        },
      };
    }

    return (
      rawSloData && {
        ...sloData,
        interimResults: {
          isLoading: fetchInterimResults,
          isMissingPreviousDayResults,
        },
      }
    );
  }, [
    rawSloData,
    interimError,
    rawInterimData,
    fetchInterimResults,
    isMissingPreviousDayResults,
    rawEventData,
    sloIncidentsData,
  ]);

  const [runbookParam, setRunbookParam] = useQueryParam('runbook', BooleanParam);
  const { onEdit } = useActions(publicId, data?.name, 'slo');

  const [isLoadingRunbook, setIsLoadingRunbook] = React.useState(!!runbookParam);
  const [showRunbookWarning, setShowRunbookWarning] = React.useState(false);
  const [showLowResolutionWarning, setShowLowResolutionWarning] = React.useState(false);

  React.useEffect(() => {
    if (runbookParam && data?.runbookUrl) {
      window.location.href = data.runbookUrl;
    } else if (data && !data?.runbookUrl) {
      setIsLoadingRunbook(false);
      if (runbookParam) {
        setShowRunbookWarning(true);
        setRunbookParam(undefined);
      }
    } else if (!runbookParam && data?.runbookUrl) {
      setShowRunbookWarning(false);
    }
  }, [data, runbookParam, setRunbookParam]);

  useEffect(() => {
    if (error instanceof FetchError && error.response.status === 404) {
      onNavigation('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (!data?.indicator?.dataSourceQueryReason || !data?.quality?.sloQualityResultItems) {
      setShowLowResolutionWarning(false);
      return;
    }

    const isResolutionDependent = data.quality.sloQualityResultItems.find(
      (item: { qualityCheckerId: string }) =>
        item.qualityCheckerId === SloQualityCheckerId.ResolutionDependentOperators
    )?.hasQualityIssue;

    // Should only show warning if the latest collection reason is low resolution and the query is resolution dependent
    if (
      data.indicator.dataSourceQueryReason !==
        IndicatorDTOHackDataSourceQueryReasonEnum.CollectionLowResolution ||
      !isResolutionDependent
    ) {
      setShowLowResolutionWarning(false);
      return;
    }

    setShowLowResolutionWarning(true);
  }, [data]);

  // Transform cohortHierarchy to breadth-first traversal (BFS) array of cohort
  const { cohortHierarchy } = data || {};
  const cohortTypeSorted: string[] = useMemo(
    () => (cohortHierarchy ? cohortHierarchyToBFSData(cohortHierarchy) : []),
    [cohortHierarchy]
  );

  if (!rawSloData || sloIsLoading || isLoadingRunbook) return <SkeletonBlock />;

  return (
    <ExperienceErrorBoundary>
      <WithPageTitle title={data.name} scope="SLO">
        <div css={styles.root}>
          <LowPriorityWarning isLowPriority={data.isLowPriority} />
          <div css={styles.parentBlock}>
            <KeyValue title="Parent Capability">
              <CapabilityEntityLink
                name={data.parent.name}
                publicId={data.parent.publicId}
                shouldTruncate
                truncateLength="750px"
              />
            </KeyValue>
          </div>
          {isSLOGroupMemberPage && (
            <div css={[flexStart, spaceTopComfortable]}>
              <Button
                appearance="primary"
                onClick={() => {
                  onNavigation(`/slo-${isMimir ? 'mimir' : 'group'}/${sloGroupPublicId}`);
                }}
              >
                <span css={[flexStart, spaceGapMinimum]}>
                  <ChildIssuesIcon size="small" label="" />
                  <span>View SLO group page</span>
                </span>
              </Button>
            </div>
          )}
          <div css={isSLOGroupMemberPage ? spaceTopCozy : spaceTopComfortable}>
            <SloPageHeader
              publicId={data.publicId}
              name={data.name}
              type={data.type}
              performance={data.performance}
              objective={data.objective}
              indicator={indicator}
              sloLifecycleStatus={getSLOLifecycleStatus(data.isDraft, data.isSnapshot)}
              isAlertable={data.indicator?.isAlertable}
              isSkeleton={data.isSkeleton}
              parentCapabilityPublicId={data.parent.publicId}
              lowTrafficStatus={lowTrafficStatusData}
              isMimir={isMimir}
              sloInstance={data.sloInstance}
              cohortTypeSorted={cohortTypeSorted}
            />
          </div>
          {/* SLO title */}
          <div css={[spaceTopMinimum, breakWord]}>
            <h3>{data.name}</h3>
          </div>
          {showRunbookWarning && (
            <div css={[spaceTopComfortable]}>
              <SectionMessage
                appearance="warning"
                actions={[
                  <SectionMessageAction key="add-runbook-url" onClick={onEdit}>
                    Add Runbook URL
                  </SectionMessageAction>,
                ]}
              >
                The Runbook URL for this SLO has not been configured.
              </SectionMessage>
            </div>
          )}
          {data.description && (
            <div css={[spaceTopComfortable, styles.descriptionBlock]}>
              <Item title="Description">
                <p>{stringReplaceLink(data.description)}</p>
              </Item>
            </div>
          )}
          <div css={spaceTopComfortable}>
            <SloPageSummary
              performance={data.performance}
              objective={data.objective}
              isSkeleton={data.isSkeleton}
              quality={data.quality}
              interimResults={data.interimResults}
            />
          </div>
          <LowTrafficHelper
            publicId={data.publicId}
            objective={data.objective}
            lowTrafficStatus={lowTrafficStatusData}
          />
          {showLowResolutionWarning && (
            <div css={spaceTopComfortable}>
              <SectionMessage appearance="warning">
                Data for this SLO has been retrieved at a daily resolution. As your SLO uses{' '}
                <ExternalLinkAction
                  text="resolution-dependent operators"
                  href="https://developer.atlassian.com/platform/slos/tome/slo-quality/#resolution-dependent-operators"
                />
                , you might see data discrepancies with SignalFx until minutely datapoints have been
                retrieved.
              </SectionMessage>
            </div>
          )}
          {isHighCardinality(data.quality) && <HighCardinalityWarning />}
          <div css={styles.sloPageDataSectionStyles}>
            <SloPageData
              events={events}
              name={data.name}
              publicId={data.publicId}
              indicator={data.indicator}
              interimResults={fetchInterimResults && data.interimResults}
              performance={data.performance}
              objective={data.objective}
              quality={data.quality}
              tags={data.tag}
              type={data.type}
              serviceId={data.serviceId}
              suppressLowTrafficAlerts={data.suppressLowTrafficAlerts}
              isUltraSteepBurnEnabled={data.isUltraSteepBurnEnabled}
              lowTrafficThreshold={data.lowTrafficThreshold}
              isSloGroupMemberPage={isSLOGroupMemberPage}
              enabledAlerts={data.enabledAlerts}
              sloLifecycleStatus={getSLOLifecycleStatus(data.isDraft, data.isSnapshot)}
              pollinatorUuids={data.pollinatorUuids}
              pollinatorArchetypeUuids={data.pollinatorArchetypeUuids}
            />
          </div>
          {!isSLOGroupMemberPage && (
            <div css={spaceTopComfortable}>
              {publicId && <RevisionLogs withTitle lazyload entityType="slo" publicId={publicId} />}
            </div>
          )}
        </div>
      </WithPageTitle>
    </ExperienceErrorBoundary>
  );
};

export default SLOView;
